/**
 * WorkforceRelationships — the staff member's relationship book ("Customer
 * Relationships" for a bank tenant; the person-noun is TENANT VOCABULARY, served,
 * so the same screen reads "Student Relationships" for a university tenant)
 * (Personal Banker). Verbatim port of the prototype's MyRelationshipsScreen
 * (relationships-screen.jsx): the advisor's book of members — the key things to
 * remember for the next touchpoint, plus the scheduled follow-ups they owe.
 *
 * P0.1 of the WFM reconciliation handoff (README): the master had no equivalent
 * screen. Structure ported faithfully — KPI strip, a "Follow-ups to call" list
 * (Snooze 7d / Log contact per row), and per-member cards (key-fact add/remove,
 * a Set-follow-up popover at 3/7/14/30 days, Log contact). Rendered inside the
 * real shell, wired to declared ops through wf-api (awaited — no fake
 * interactivity), with dates carried as ISO YYYY-MM-DD per the wf-shared
 * wall-clock convention (the prototype's Date-object math is the one adaptation).
 *
 * Ops (REQ-050, pending in the data layer; preview-seeded for the bank tenant):
 * getMemberBook, logContact, snoozeFollowUp, scheduleFollowUp, addMemberFact,
 * removeMemberFact.
 */
import { useEffect, useState } from "react";
import { Avatar, Btn, Icon, Pill, T, alpha } from "@v0y4ge/jrni-ui";
import { Screen, PageHeader } from "../../shell/chrome";
import { DataSourceStrip } from "../../shell/opSource";
import { EmptyState } from "../../shell/composites";
import { toast } from "../../shell/toast";
import { useWfResource, useBusy } from "./useWorkforce";
import {
  getMemberBook, logContact, snoozeFollowUp, scheduleFollowUp, addMemberFact, removeMemberFact,
  type MemberBookResponse, type MemberBookEntry, type MemberFollowUp,
} from "./wf-api";
import { isoToday, addDaysIso, dayLabel } from "./wf-shared";
import { useT } from "../../lib/i18n";
import WorkforceEventsDrawer from "./WorkforceEventsDrawer";
import { useTenantVocab } from "../../lib/tenant-vocab";
import { useTenantLabels } from "../../lib/labels";

const CHURN_TONE: Record<MemberBookEntry["churnRisk"], "success" | "warn" | "danger"> = { low: "success", med: "warn", high: "danger" };
const REMIND_OPTS = [3, 7, 14, 30];

/** Stable, presentation-only avatar hue per member name. */ // ui-config
const MEMBER_HUES = [T.tPurple, T.tOrange, T.tPink, T.tDarkBlue, T.tTurquoise, T.tGreen];
function memberHue(name: string): string {
  const n = (name || "").split("").reduce((a, c) => a + c.charCodeAt(0), 0);
  return MEMBER_HUES[n % MEMBER_HUES.length];
}

function daysUntil(iso: string): number {
  const a = new Date(iso + "T00:00:00").getTime();
  const b = new Date(isoToday() + "T00:00:00").getTime();
  return Math.round((a - b) / 86400000);
}
function fmtDue(iso: string, t: (k: string, f: string) => string): { label: string; rel: string; tone: "danger" | "warn" | "soft" } {
  const d = daysUntil(iso);
  const label = dayLabel(iso, "md");
  // i18n-icu-pending (REQ-058): the parameterized rel strings are ICU
  // (`workforce.relationships.dueRel.*`) — no fallback form exists for ICU, so
  // they ship literal until the keys land in the bundled catalog.
  if (d < 0) return { label, rel: `${Math.abs(d)}d overdue`, tone: "danger" };
  if (d === 0) return { label, rel: t("workforce.relationships.dueToday", "Due today"), tone: "warn" };
  if (d <= 3) return { label, rel: `Due in ${d}d`, tone: "warn" };
  return { label, rel: `In ${d}d`, tone: "soft" };
}

function Kpi({ label, value, color }: { label: string; value: number; color?: string }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 110 }}>
      <span style={{ fontSize: 10.5, color: T.soft, textTransform: "uppercase", letterSpacing: 0.06, fontWeight: 700 }}>{label}</span>
      <span style={{ fontSize: 24, fontWeight: 700, color: color ?? T.n1, fontFamily: T.fontMono }}>{value}</span>
    </div>
  );
}

export default function WorkforceRelationships() {
  const t = useT();
  /* Tenant vocabulary (the noun for the person this screen is about) comes from the
     SERVED catalog — getTenantVocabulary's customers.entity/entityPlural (REQ-091,
     tenant-scoped) — with lib/labels' jargon override as the fallback layer and a
     byte-exact English default under both. A bank tenant → Customer/Customers,
     a university tenant → Student/Students, a retail tenant → Customer/Customers, with
     zero further code changes here.

     Copy still goes through t(key, fallback); the noun is a {customer}/{customers}
     placeholder resolved after lookup (the QueueConsole subtitle precedent), NOT an
     ICU call site — ICU keys have no fallback form, so a locale missing the key would
     render the raw key. */
  const vocabData = useTenantVocab();
  const lbl = useTenantLabels();
  const one = vocabData?.customers?.entity ?? lbl("customer", "Customer");
  const many = vocabData?.customers?.entityPlural ?? lbl("customers", "Customers");
  const vt = (key: string, fallback: string) => t(key, fallback)
    .split("{customers}").join(many)
    .split("{customer}").join(one)
    .split("{lowerCustomers}").join(many.toLowerCase())
    .split("{lowerCustomer}").join(one.toLowerCase());
  const { data, loading, error } = useWfResource<MemberBookResponse>("memberbook", getMemberBook);
  const { busy, run } = useBusy();

  // Local working copy hydrated from the fetch; every mutation awaits its op and
  // then reconciles this copy (success UI only after the call resolves).
  const [members, setMembers] = useState<MemberBookEntry[]>([]);
  const [followUps, setFollowUps] = useState<MemberFollowUp[]>([]);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [schedFor, setSchedFor] = useState<string | null>(null); // ui-local (popover)
  const [eventsFor, setEventsFor] = useState<string | null>(null); // ui-local (events drawer, prefilled with this customer)

  useEffect(() => {
    if (!data) return;
    setMembers(data.members);
    setFollowUps(data.followUps);
  }, [data]);

  const openFollowUps = followUps.filter((f) => !f.done).sort((a, b) => (a.due < b.due ? -1 : 1));
  const overdue = openFollowUps.filter((f) => daysUntil(f.due) < 0).length;
  const dueThisWeek = openFollowUps.filter((f) => { const d = daysUntil(f.due); return d >= 0 && d <= 7; }).length;
  const oppCount = members.filter((m) => m.hasOpportunity).length;

  const err = (e: unknown) => toast({ tone: "danger", title: t("workforce.relationships.somethingWentWrong", "Something went wrong"), subtitle: e instanceof Error ? e.message : String(e) });

  const doLogContact = (f: MemberFollowUp) => void run(`contact:${f.id}`, async () => {
    try { await logContact(f.memberId, f.id); setFollowUps((arr) => arr.map((x) => x.id === f.id ? { ...x, done: true } : x)); toast({ tone: "success", title: t("workforce.relationships.followUpLogged", "Follow-up logged"), subtitle: vt("workforce.relationships.vocabLoggedSub", "Marked complete and saved to the {lowerCustomer}'s history") }); }
    catch (e) { err(e); }
  });
  const doSnooze = (f: MemberFollowUp) => void run(`snooze:${f.id}`, async () => {
    try { const r = await snoozeFollowUp(f.id, 7); const due = r.due || addDaysIso(f.due, 7); setFollowUps((arr) => arr.map((x) => x.id === f.id ? { ...x, due } : x)); toast({ tone: "info", title: t("workforce.relationships.snoozed7Days", "Snoozed 7 days"), subtitle: t("workforce.relationships.reminderMovedOut", "Reminder moved out") }); }
    catch (e) { err(e); }
  });
  const doSchedule = (m: MemberBookEntry, days: number) => void run(`sched:${m.id}`, async () => {
    try { const r = await scheduleFollowUp(m.id, days); setFollowUps((arr) => [...arr, r.followUp]); setSchedFor(null); toast({ tone: "success", title: t("workforce.relationships.followUpScheduled", "Follow-up scheduled"), subtitle: `${m.name} · ${dayLabel(addDaysIso(isoToday(), days), "md")}` }); }
    catch (e) { err(e); }
  });
  const doAddFact = (m: MemberBookEntry) => {
    const v = (drafts[m.id] || "").trim();
    if (!v) return;
    void run(`fact:${m.id}`, async () => {
      try { await addMemberFact(m.id, v); setMembers((arr) => arr.map((x) => x.id === m.id ? { ...x, keyFacts: [...x.keyFacts, v] } : x)); setDrafts((d) => ({ ...d, [m.id]: "" })); }
      catch (e) { err(e); }
    });
  };
  const doRemoveFact = (m: MemberBookEntry, i: number) => void run(`fact:${m.id}:${i}`, async () => {
    try { await removeMemberFact(m.id, i); setMembers((arr) => arr.map((x) => x.id === m.id ? { ...x, keyFacts: x.keyFacts.filter((_, j) => j !== i) } : x)); }
    catch (e) { err(e); }
  });
  const doLogContactMember = (m: MemberBookEntry) => void run(`mcontact:${m.id}`, async () => {
    try { await logContact(m.id); toast({ tone: "success", title: t("workforce.relationships.contactLogged", "Contact logged"), subtitle: `${m.name} · ${t("workforce.relationships.savedToRelationshipHistory", "saved to relationship history")}` }); }
    catch (e) { err(e); }
  });

  const card: React.CSSProperties = { border: `1px solid ${T.border}`, borderRadius: 12, background: T.white };

  return (
    <Screen rail="wf-myrelationships">
      <PageHeader
        /* Title matches the rail label BYTE-FOR-BYTE, capitalisation included (operator,
           2026-08-03): the rail says "Customer Relationships", so the screen does too.
           `shell.rail.wfMycustrel` is the rail's own key but the kit catalog doesn't define
           it, so the literal is the source of truth on both sides for now. */
        title={vt("workforce.relationships.vocabTitle", "{customer} Relationships")}
        subtitle={vt("workforce.relationships.vocabSubtitle", "Your book of {lowerCustomers} — key things to know for the next touchpoint, and the follow-ups you owe them.")}
        /* `false`, not `null`: PageHeader treats null as "not supplied" and falls through to
           its default "← Dashboard" link (chrome.tsx:62). Removed 2026-08-03 (operator) — the
           Personal Banker rail has no Dashboard. */
        breadcrumb={false}
      />
      <DataSourceStrip screen="wfrelationships" ops={["getMemberBook", "logContact", "snoozeFollowUp", "scheduleFollowUp", "addMemberFact", "removeMemberFact"]} reqId="REQ-050" />
      <div style={{ flex: 1, overflow: "auto", padding: "14px 24px 32px", display: "flex", flexDirection: "column", gap: 20 }}>
        {error ? <div style={{ padding: 40, textAlign: "center", color: T.soft }}>{vt("workforce.relationships.vocabLoadError", "Couldn't load your {lowerCustomer} book:")}{" "}{error}</div>
          : loading && members.length === 0 ? <div style={{ padding: 40, textAlign: "center", color: T.soft, fontSize: 14 }}>{t("common.status.loading", "Loading…")}</div>
          : <>
            {/* KPI strip */}
            <div style={{ display: "flex", gap: 40, padding: "18px 22px", flexWrap: "wrap", ...card }}>
              <Kpi label={vt("workforce.relationships.vocabCount", "{customers}")} value={members.length} />
              <Kpi label={t("workforce.relationships.followUpsDue7d", "Follow-ups due (7d)")} value={dueThisWeek} color={dueThisWeek ? T.p1 : T.n1} />
              <Kpi label={t("workforce.relationships.overdue", "Overdue")} value={overdue} color={overdue ? T.red1 : T.green1} />
              <Kpi label={t("workforce.relationships.openOpportunities", "Open opportunities")} value={oppCount} color={oppCount ? T.orange1 : T.n1} />
            </div>

            {/* Follow-ups to call */}
            <div style={{ ...card, overflow: "hidden" }}>
              <div style={{ padding: "16px 22px 12px", borderBottom: openFollowUps.length ? `1px solid ${T.n6}` : "none" }}>
                <h3 style={{ fontSize: 16, fontWeight: 700, color: T.n1, margin: 0 }}>{t("workforce.relationships.followUpsToCall", "Follow-ups to call")}</h3>
                <p style={{ fontSize: 12.5, color: T.soft, margin: "5px 0 0" }}>{vt("workforce.relationships.vocabFollowUpsSub", "Reminders to contact a {lowerCustomer} — from opportunities you flagged but didn't book, and scheduled check-ins.")}</p>
              </div>
              {openFollowUps.length === 0 ? (
                <div style={{ padding: "10px 0 6px" }}><EmptyState icon={<Icon.check size={18} />} title={t("workforce.relationships.allCaughtUp", "All caught up")} body={t("workforce.relationships.noFollowUpsOutstanding", "No follow-ups outstanding right now.")} /></div>
              ) : openFollowUps.map((f, idx) => {
                const due = fmtDue(f.due, t);
                return (
                  <div key={f.id} style={{ display: "grid", gridTemplateColumns: "auto 1fr auto", gap: 14, alignItems: "center", padding: "14px 22px", borderTop: idx ? `1px solid ${T.n6}` : "none" }}>
                    <Avatar initials={f.initials} color={memberHue(f.member)} size={38} />
                    <div style={{ display: "flex", flexDirection: "column", gap: 3, minWidth: 0 }}>
                      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                        <span style={{ fontSize: 14, fontWeight: 600, color: T.n1 }}>{f.member}</span>
                        <Pill tone={due.tone === "soft" ? "neutral" : due.tone}>{due.rel} · {due.label}</Pill>
                      </div>
                      <span style={{ fontSize: 13, color: T.n2, lineHeight: 1.45 }}>{f.reason}</span>
                      <span style={{ fontSize: 12, color: T.soft }}>{t("workforce.relationships.bestVia", "Best via")}{" "}{f.channel}</span>
                    </div>
                    <div style={{ display: "flex", gap: 8, flexShrink: 0, flexWrap: "wrap", justifyContent: "flex-end" }}>
                      <Btn variant="secondary" size="sm" disabled={busy !== null} onClick={() => doSnooze(f)}>{busy === `snooze:${f.id}` ? "…" : t("workforce.relationships.snooze7d", "Snooze 7d")}</Btn>
                      <Btn size="sm" disabled={busy !== null} onClick={() => doLogContact(f)}><Icon.check size={13} /> {busy === `contact:${f.id}` ? "…" : t("workforce.relationships.logContact", "Log contact")}</Btn>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Member cards */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(360px, 1fr))", gap: 18 }}>
              {members.map((m) => (
                <div key={m.id} style={{ ...card, padding: 18, display: "flex", flexDirection: "column", gap: 14 }}>
                  <div style={{ display: "flex", gap: 12, alignItems: "flex-start", justifyContent: "space-between" }}>
                    <div style={{ display: "flex", gap: 12, alignItems: "center", minWidth: 0 }}>
                      <Avatar initials={m.initials} color={memberHue(m.name)} size={44} />
                      <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
                        <span style={{ fontSize: 16, fontWeight: 700, color: T.n1 }}>{m.name}</span>
                        <span style={{ fontSize: 12, color: T.soft }}>{m.tier} · {m.tenure} · {m.products} {t("workforce.relationships.products", "products")}</span>
                      </div>
                    </div>
                    <Pill tone={CHURN_TONE[m.churnRisk]}>{m.churnRisk} {t("workforce.relationships.churn", "churn")}</Pill>
                  </div>

                  {/* Key things to know */}
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    <span style={{ fontSize: 10.5, color: T.soft, textTransform: "uppercase", letterSpacing: 0.06, fontWeight: 700 }}>{t("workforce.relationships.keyThingsToKnow", "Key things to know")}</span>
                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                      {m.keyFacts.map((k, i) => (
                        <div key={i} style={{ display: "flex", gap: 8, alignItems: "flex-start", background: T.n6, borderRadius: 8, padding: "8px 10px" }}>
                          <span style={{ width: 5, height: 5, borderRadius: 999, background: T.p1, marginTop: 7, flexShrink: 0 }} />
                          <span style={{ fontSize: 13, color: T.n2, lineHeight: 1.45, flex: 1 }}>{k}</span>
                          <button aria-label={t("common.actions.remove", "Remove")} disabled={busy !== null} onClick={() => doRemoveFact(m, i)} style={{ border: 0, background: "transparent", cursor: "pointer", color: T.soft, padding: 2, flexShrink: 0, display: "inline-flex" }}><Icon.x size={12} /></button>
                        </div>
                      ))}
                    </div>
                    <div style={{ display: "flex", gap: 8 }}>
                      <input value={drafts[m.id] || ""} onChange={(e) => setDrafts((d) => ({ ...d, [m.id]: e.target.value }))}
                        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); doAddFact(m); } }}
                        placeholder={t("workforce.relationships.addSomethingToRemember", "Add something to remember…")}
                        style={{ flex: 1, minWidth: 0, padding: "8px 12px", fontSize: 13, fontFamily: T.font, border: `1px solid ${T.n5}`, borderRadius: 8, outline: "none", color: T.n1, background: T.white, boxSizing: "border-box" }} />
                      <Btn variant="secondary" size="sm" disabled={busy !== null || !(drafts[m.id] || "").trim()} onClick={() => doAddFact(m)}>{t("common.actions.add", "Add")}</Btn>
                    </div>
                  </div>

                  {/* Last touch */}
                  {m.lastNote ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: 3, paddingTop: 12, borderTop: `1px solid ${T.n6}` }}>
                      <span style={{ fontSize: 10.5, color: T.soft, textTransform: "uppercase", letterSpacing: 0.06, fontWeight: 700 }}>{t("workforce.relationships.lastTouch", "Last touch")}{" "}{m.lastWhen ? /* TODO(i18n): replace template-literal with t('workforce.relationships.text', { ... }) */` · ${m.lastWhen}` : ""}</span>
                      <span style={{ fontSize: 13, color: T.n2, lineHeight: 1.45 }}>{m.lastNote}</span>
                    </div>
                  ) : null}

                  {/* Footer actions */}
                  <div style={{ display: "flex", gap: 8, paddingTop: 12, borderTop: `1px solid ${T.n6}`, position: "relative", flexWrap: "wrap" }}>
                    <Btn size="sm" disabled={busy !== null} onClick={() => setSchedFor(schedFor === m.id ? null : m.id)}><Icon.bell size={13} /> {t("workforce.relationships.setFollowUp", "Set follow-up")}</Btn>
                    <Btn variant="secondary" size="sm" disabled={busy !== null} onClick={() => doLogContactMember(m)}>{busy === `mcontact:${m.id}` ? "…" : t("workforce.relationships.logContact", "Log contact")}</Btn>
                    {/* Entry point 3 for the events drawer (REQ-127): the relationship card.
                        This is where he decides what a customer needs next, so it is where
                        "send them to an event" belongs. Prefills their name. */}
                    <Btn variant="secondary" size="sm" testid="wfrel-invite-event" disabled={busy !== null} onClick={() => setEventsFor(m.name)}><Icon.ticket size={13} /> {t("workforce.relationships.inviteToEvent", "Invite to an event")}</Btn>
                    {schedFor === m.id ? (
                      <div style={{ position: "absolute", left: 0, top: "calc(100% + 6px)", zIndex: 20, background: T.white, border: `1px solid ${T.n5}`, borderRadius: 10, boxShadow: T.shadowMd, padding: 12, width: 240 }}>
                        <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: 0.06, textTransform: "uppercase", color: T.soft, marginBottom: 8 }}>{t("workforce.relationships.remindMeToContactIn", "Remind me to contact in")}</div>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                          {REMIND_OPTS.map((d) => (
                            <button key={d} disabled={busy !== null} onClick={() => doSchedule(m, d)} style={{ flex: "1 0 40%", padding: "7px 8px", borderRadius: 8, border: `1px solid ${T.n5}`, background: T.white, color: T.n1, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: T.font, textAlign: "center" }}>
                              {d} {t("workforce.relationships.days", "days")}
                              <div style={{ fontSize: 10, fontWeight: 500, color: T.soft, marginTop: 1 }}>{dayLabel(addDaysIso(isoToday(), d), "md")}</div>
                            </button>
                          ))}
                        </div>
                      </div>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
          </>}
      </div>
      {eventsFor !== null ? (
        <WorkforceEventsDrawer customer={eventsFor || null} onClose={() => setEventsFor(null)} />
      ) : null}
    </Screen>
  );
}
